import React, { useState, useRef, useEffect } from "react";
import { WebApiURL } from "../Constants/Constants";

function ResetPasswordForm({ setActiveForm }) {
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const inputsRef = useRef([]);

  // --- OTP input handlers ---
  const handleChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // --- Step 1: Request OTP ---
  const handleRequestOtp = async () => {
    if (!email) {
      setStatusMessage("Please enter your email.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${WebApiURL}Verification/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatusMessage("✅ OTP sent to your email.");
        setOtpSent(true);
        localStorage.setItem("email", email);
      } else {
        setStatusMessage(data.message || "Failed to send OTP.");
      }
    } catch {
      setStatusMessage("Network error.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Step 2: Verify OTP ---
  const handleVerifyOtp = async () => {
    const otp = otpDigits.join("");
    if (otp.length < 6) {
      setStatusMessage("Please enter full OTP.");
      return;
    }
    setIsSubmitting(true);
    try {
      const emailStored = localStorage.getItem("email");
      if (!emailStored) {
        setStatusMessage("Session expired. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const res = await fetch(`${WebApiURL}Verification/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Email: emailStored, OTP: otp }),
      });
      const data = await res.json();

      if (res.ok) {
        setStatusMessage("✅ OTP verified! Enter your new password.");
        setOtpVerified(true);
      } else {
        setStatusMessage(data.message || "Invalid OTP. Try again.");
      }
    } catch {
      setStatusMessage("Network error.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Step 3: Reset Password ---
  const handleResetPassword = async () => {
  if (!email) {
    setStatusMessage("Please enter your email.");
    return;
  }
  if (!newPassword || !confirmPassword) {
    setStatusMessage("Please enter both password fields.");
    return;
  }
  if (newPassword !== confirmPassword) {
    setStatusMessage("Passwords do not match.");
    return;
  }

  setIsSubmitting(true);
  try {
    const res = await fetch(`${WebApiURL}Verification/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        Email: email,
        NewPassword: newPassword,
      }),
    });

    const data = await res.json();
    if (res.ok) {
      setStatusMessage("✅ Password updated successfully! Redirecting to login...");
      setTimeout(() => setActiveForm("login"), 1500);
    } else {
      setStatusMessage(data.message || "Failed to reset password.");
    }
  } catch {
    setStatusMessage("Network error.");
  } finally {
    setIsSubmitting(false);
  }
};


  // --- Resend OTP ---
  const handleResend = async () => {
    setIsResending(true);
    setStatusMessage("");
    try {
      const emailStored = localStorage.getItem("email");
      if (!emailStored) {
        setStatusMessage("Session expired. Please try again.");
        setIsResending(false);
        return;
      }
      const res = await fetch(`${WebApiURL}Verification/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailStored }),
      });
      const data = await res.json();
      if (res.ok) {
        setOtpDigits(["", "", "", "", "", ""]);
        inputsRef.current[0]?.focus();
        setStatusMessage("✅ OTP resent successfully!");
      } else {
        setStatusMessage(data.message || "Failed to resend OTP.");
      }
    } catch {
      setStatusMessage("Network error.");
    } finally {
      setIsResending(false);
    }
  };

  // --- Clear status after 5s ---
  useEffect(() => {
    if (!statusMessage) return;
    const timer = setTimeout(() => setStatusMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  return (
    <div className="max-564 w-100 rounded-1 border p-4">
      <div className="font-24 font-700 mb-4 text-center text-primary-green text-uppercase border-bottom pb-2">
        Reset Password
      </div>

      {statusMessage && (
        <div className={`alert ${statusMessage.includes("✅") ? "alert-success" : "alert-danger"}`}>
          {statusMessage}
        </div>
      )}

      {!otpSent && (
        <div className="mb-3">
          <input
            type="email"
            className="form-control"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className="btn btn-primary-green w-100 mt-2" onClick={handleRequestOtp} disabled={isSubmitting}>
            {isSubmitting ? "Sending OTP..." : "Send OTP"}
          </button>
        </div>
      )}

      {otpSent && !otpVerified && (
        <div className="form-group mb-3">
          <label className="form-label font-17 font-400 text-primary-green mb-1">Enter OTP</label>
          <div className="d-flex justify-content-between gap-2 mb-2">
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                maxLength="1"
                className="form-control text-center height-40 rounded-1 border-color-7D9199"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                ref={(el) => (inputsRef.current[index] = el)}
              />
            ))}
          </div>
          <div className="d-flex justify-content-between mt-2">
            <button className="btn btn-primary-green" onClick={handleVerifyOtp} disabled={isSubmitting}>
              {isSubmitting ? "Verifying..." : "Verify OTP"}
            </button>
            <button className="btn btn-outline-primary-green" onClick={handleResend} disabled={isResending}>
              {isResending ? "Resending..." : "Resend OTP"}
            </button>
          </div>
        </div>
      )}

      {otpVerified && (
        <div className="mb-3">
          <input
            type="password"
            className="form-control mb-2"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input
            type="password"
            className="form-control mb-2"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button className="btn btn-primary-green w-100" onClick={handleResetPassword} disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Reset Password"}
          </button>
        </div>
      )}

      <button
        type="button"
        className="text-decoration-none text-primary-green font-17 font-400 bg-white border-0 mt-2"
        onClick={() => setActiveForm("login")}
      >
        ← Back to Login
      </button>
    </div>
  );
}

export default ResetPasswordForm;
